import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AttachMoney,
  Check,
  Info,
  WhatsApp,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { CommonDialog } from "./CommonDialog";
import { useContractContext } from "@/contexts/ContractContext";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { useKeepPaymentModalOpenAfterCharge } from "@/hooks/useKeepPaymentModalOpenAfterCharge";
import { useInternationalVerify } from "@/utils/useInternationalVerify";
import { isChargeNotificationSentOnAppointment } from "@/utils/chargeNotificationSent";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import { toast } from "react-toastify";
import { useFormatter } from "@/utils/useFormatter";

/** Dados da consulta para montar o aviso manual no WhatsApp Web. */
export interface PaymentPendingWhatsAppNoticeContext {
  practitionerName?: string;
  appointmentStart?: string;
  locationLabel?: string;
  /** Nome do paciente para saudação no WhatsApp. */
  patientName?: string;
  /** Telefone do paciente (ou tutor no vet) para abrir o chat direto no WhatsApp Web. */
  patientPhone?: string;
}

function formatPractitionerWithTitle(name?: string): string {
  const trimmed = (name ?? "").trim();
  if (
    !trimmed ||
    trimmed === "undefined" ||
    trimmed === "Dr(a). undefined" ||
    trimmed === "Dr(a)."
  ) {
    return "Dr(a). profissional não informado";
  }
  const withoutPrefix = trimmed.replace(/^Dr\.\s*/gi, "").trim();
  const formattedName = useFormatter.formatNameDisplay(withoutPrefix);
  if (!formattedName || formattedName === "undefined") {
    return "Dr(a). profissional não informado";
  }
  return `Dr(a). ${formattedName}`;
}

function formatPatientGreetingName(name?: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed || trimmed === "undefined") {
    return "paciente";
  }
  return useFormatter.formatNameDisplay(trimmed) || "paciente";
}

function buildWhatsAppPaymentNoticeUrl(
  context: PaymentPendingWhatsAppNoticeContext | undefined,
  paymentLink?: string,
): string {
  const patientName = formatPatientGreetingName(context?.patientName);
  const practitioner = formatPractitionerWithTitle(context?.practitionerName);
  const dateTime = context?.appointmentStart
    ? dayjs(context.appointmentStart)
        .locale("pt-br")
        .format("DD/MM/YYYY [às] HH:mm")
    : "data não informada";

  const paymentLine =
    paymentLink?.trim()
      ? `Acesse o link e realize o pagamento: ${paymentLink.trim()}\n`
      : "";

  const message =
    `Olá, ${patientName}\n` +
    `Sua consulta com o ${practitioner} está agendada para o dia ${dateTime}h.\n` +
    paymentLine +
    `Acompanhe seu agendamento em https://app.fluxmed.com.br ou pelo app Fluxmed (disponível na Google Play e App Store).`;

  const encodedText = encodeURIComponent(message);
  const rawPhone = context?.patientPhone?.trim();

  if (!rawPhone) {
    return `https://web.whatsapp.com/send?text=${encodedText}`;
  }

  const phone = useInternationalVerify.verifyPhoneDDI(rawPhone);
  return `https://web.whatsapp.com/send?phone=+${phone}&text=${encodedText}`;
}

function getSlotReferenceFromAppointment(
  appointment?: fhir5.Appointment | null,
): string {
  return appointment?.slot?.[0]?.reference ?? "";
}

interface PaymentPendingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmPayment: () => Promise<void>;
  /** Em falha, rejeitar a Promise (após setar erro no pai). */
  onSendCharge: () => Promise<void>;
  loadingSendCharge?: boolean;
  errorSendCharge?: string;
  loadingConfirmPayment?: boolean;
  notificationSent?: boolean;
  appointment?: fhir5.Appointment | null;
  hideConfirmPayment?: boolean;
  /** Valor da cobrança exibido no painel administrativo (/stats). */
  chargeAmount?: number;
  /** Contexto da consulta para aviso manual via WhatsApp Web. */
  whatsappNoticeContext?: PaymentPendingWhatsAppNoticeContext;
}

export const PaymentPendingModal: React.FC<PaymentPendingModalProps> = ({
  isOpen,
  onClose,
  onConfirmPayment,
  onSendCharge,
  loadingSendCharge = false,
  errorSendCharge,
  loadingConfirmPayment = false,
  notificationSent = false,
  appointment,
  hideConfirmPayment = false,
  chargeAmount,
  whatsappNoticeContext,
}) => {
  const { currentContract } = useContractContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const { contractPostAttendance } = usePostAttendanceContext();
  const hasFluxZapExtension = !!contractPostAttendance;
  const requiresChargeSentBeforeConfirm = useKeepPaymentModalOpenAfterCharge();

  const [chargeSentThisSession, setChargeSentThisSession] = useState(false);
  const [loadingWhatsAppNotice, setLoadingWhatsAppNotice] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setChargeSentThisSession(false);
      setLoadingWhatsAppNotice(false);
    }
  }, [isOpen]);

  /**
   * Agenda sem custo: não há cobrança a enviar; vários pontos do app setam mensagens
   * ligeiramente diferentes, mas todas citam "agenda sem custo".
   * Nesse caso o botão "Confirmar pagamento" deve ficar habilitado sem enviar cobrança.
   */
  const isZeroCostSchedule =
    typeof errorSendCharge === "string" &&
    errorSendCharge.includes("agenda sem custo");

  /** Aviso e confirmação: extension notification-sent no Appointment (fallback: prop legada). */
  const showChargeNotificationSentNotice = useMemo(() => {
    if (appointment != null) {
      return isChargeNotificationSentOnAppointment(appointment);
    }
    return notificationSent;
  }, [appointment, notificationSent]);

  // No contexto específico: só libera confirmar após cobrança enviada — exceto agenda sem custo
  const chargeRequirementMet =
    isZeroCostSchedule ||
    (requiresChargeSentBeforeConfirm
      ? chargeSentThisSession
      : showChargeNotificationSentNotice || chargeSentThisSession);

  const handleSendChargeClick = useCallback(async () => {
    try {
      await onSendCharge();
      setChargeSentThisSession(true);
    } catch {
      // Erro e mensagem no componente pai
    }
  }, [onSendCharge]);

  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan"
    );

  const disableChargeButton = useMemo(() => {
    return (
      loadingSendCharge ||
      (typeof errorSendCharge === "string" &&
        errorSendCharge.includes("agenda sem custo"))
    );
  }, [loadingSendCharge, errorSendCharge]);

  const handleOpenWhatsAppNotice = useCallback(async () => {
    const slotReference = getSlotReferenceFromAppointment(appointment);
    if (!slotReference) {
      toast.error("Não foi possível identificar o horário da consulta.");
      return;
    }

    setLoadingWhatsAppNotice(true);
    try {
      const paymentLink = await marketplaceService.sendPaymentMessage({
        slotReference,
        patientName: "",
        healthcareDisplay: "",
        practitionerName: "",
        phoneNumber: whatsappNoticeContext?.patientPhone ?? "",
      });

      if (!paymentLink?.trim()) {
        toast.error(
          "Não foi possível gerar o link de pagamento. Por favor, tente novamente.",
        );
        return;
      }

      const whatsAppUrl = buildWhatsAppPaymentNoticeUrl(
        whatsappNoticeContext,
        paymentLink,
      );
      window.open(whatsAppUrl, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error(
          "Não é possível gerar link de pagamento para consulta em agenda sem custo.",
        );
      } else {
        toast.error(
          "Erro ao gerar link de pagamento. Por favor, tente novamente.",
        );
      }
    } finally {
      setLoadingWhatsAppNotice(false);
    }
  }, [appointment, whatsappNoticeContext]);

  const theme = useTheme();
  const showConfirmSection = !hasSubscriptionFluxPay && !hideConfirmPayment;
  const paymentConfirmHint = requiresChargeSentBeforeConfirm
    ? "Para confirmar o pagamento, a cobrança deve ser enviada antecipadamente."
    : "Confirme o pagamento apenas se a clínica já tiver recebido o valor da consulta. Esta ação não poderá ser desfeita.";

  return (
    <CommonDialog isOpen={isOpen} handleClose={onClose}>
      <Stack spacing={3} alignItems="center" p={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Info sx={{ fontSize: 40, color: "info.main" }} />
          <Typography
            variant="h6"
            fontWeight={500}
            sx={{ color: "neutral1000.main" }}
          >
            {hasFluxPayExtension
              ? "Pendente de Pagamento"
              : "Pendente de Confirmação"}
          </Typography>
        </Stack>

        <Typography variant="subtitle2" textAlign="center">
          {hasFluxPayExtension ? (
            <>
              Esta consulta está pendente de pagamento
              {chargeAmount != null && chargeAmount > 0 ? (
                <>
                  {" "}
                  (
                  <strong>
                    {chargeAmount.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </strong>
                  )
                </>
              ) : null}
              . Envie uma cobrança ao paciente ou confirme o pagamento.
            </>
          ) : (
            <>
              Esta consulta aguarda confirmação da clínica. Confirme apenas se o
              agendamento estiver correto e validado.
            </>
          )}
        </Typography>

        {hasFluxPayExtension && hasFluxZapExtension && showChargeNotificationSentNotice ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Check sx={{ color: "success700.main" }} />
            <Typography color="success700.main" variant="body2">
              Cobrança já enviada para o paciente.
            </Typography>
          </Stack>
        ) : null}

        {hasFluxPayExtension ? (
          hasFluxZapExtension ? (
            <Tooltip title="Enviar cobrança da consulta">
              <span>
                <Button
                  variant="contained"
                  sx={{ color: "neutral0.main" }}
                  startIcon={<AttachMoney />}
                  onClick={handleSendChargeClick}
                  disabled={disableChargeButton}
                >
                  Enviar cobrança
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Box
              sx={{
                px: 1,
                py: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.06),
                border: "1px dashed",
                borderColor: alpha(theme.palette.info.main, 0.35),
                width: "100%",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" textAlign="center">
                Para enviar a cobrança por WhatsApp, configure o FluxZap.{" "}
                <Link
                  component={RouterLink}
                  to="/extensions"
                  state={{ highlightFluxZap: true }}
                  onClick={onClose}
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Clique aqui
                </Link>{" "}
                para ativar.
              </Typography>
            </Box>
          )
        ) : null}

        {hasFluxPayExtension ? (
        <Box
          sx={{
            px: 2,
            py: 1.75,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.grey[500], 0.06),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.9),
            width: "100%",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Info sx={{ fontSize: 20, color: "neutral700.main", mt: 0.15, flexShrink: 0 }} />
            <Stack spacing={1.25} width="100%">
              <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                <Box component="span" fontWeight={700} color="text.primary">
                  Atenção!
                </Box>{" "}
                Oriente o paciente a acessar o Fluxmed e pagar a consulta —
                via web em{" "}
                <Link
                  href="https://app.fluxmed.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  app.fluxmed.com.br
                </Link>{" "}
                ou pelo app no celular.
              </Typography>

              <Button
                variant="outlined"
                size="small"
                startIcon={<WhatsApp />}
                onClick={handleOpenWhatsAppNotice}
                disabled={loadingWhatsAppNotice}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                Enviar aviso no WhatsApp Web
              </Button>

              {showConfirmSection ? (
                <>
                  <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.8) }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    lineHeight={1.6}
                    fontStyle="italic"
                  >
                    {paymentConfirmHint}
                  </Typography>
                </>
              ) : null}
            </Stack>
          </Stack>
        </Box>
        ) : null}

        {errorSendCharge ? (
          <Typography color="error.main" variant="body2" textAlign="center">
            {errorSendCharge}
          </Typography>
        ) : null}

        {hasFluxPayExtension && showConfirmSection ? (
          <Stack direction="column" alignItems="center" spacing={2}>
            <Tooltip
              title={
                isZeroCostSchedule
                  ? "Agenda sem custo: confirme para concluir o fluxo da consulta"
                  : requiresChargeSentBeforeConfirm && !chargeRequirementMet
                    ? "Envie a cobrança antes de confirmar o pagamento"
                    : "Confirmar pagamento da consulta"
              }
            >
              <span>
                <Button
                  variant="contained"
                  sx={{ color: "neutral0.main" }}
                  startIcon={<Check />}
                  onClick={() => {
                    onConfirmPayment();
                    onClose();
                  }}
                  // disabled={
                  //   loadingConfirmPayment ||
                  //   (requiresChargeSentBeforeConfirm && !chargeRequirementMet)
                  // }
                >
                  Confirmar pagamento
                </Button>
              </span>
            </Tooltip>
          </Stack>
        ) : null}

        {!hasFluxPayExtension && !hideConfirmPayment ? (
          <Stack direction="column" alignItems="center" spacing={2}>
            <Tooltip title="Confirmar consulta agendada">
              <span>
                <Button
                  variant="contained"
                  sx={{ color: "neutral0.main" }}
                  startIcon={<Check />}
                  onClick={() => {
                    onConfirmPayment();
                    onClose();
                  }}
                  disabled={loadingConfirmPayment}
                >
                  Confirmar consulta
                </Button>
              </span>
            </Tooltip>
          </Stack>
        ) : null}
      </Stack>
    </CommonDialog>
  );
};
